import {matchPattern} from './rule';

const targetingOwners = new WeakMap();
const personalizationOwners = new Map();

function getTargetingOwners(pubads) {
   let owners = targetingOwners.get(pubads);
   if (!owners) {
      owners = new Map();
      targetingOwners.set(pubads, owners);
   }
   return owners;
}

function sameTargeting(left = [], right = []) {
   return left.length === right.length && left.every((value, index) => value === right[index]);
}

function findActivePreviousOwner(record) {
   let previous = record?.previous;
   while (previous && (previous.invalidated || !managerIsCurrent(previous.manager))) {
      previous = previous.previous;
   }
   return previous;
}

function managerIsCurrent(manager) {
   return !manager?.disposed
      && manager.api?.clientShortName === manager.clientShortName
      && manager.api?.visitorId === manager.visitorId;
}

function getAdCampaigns(tracker) {
   return tracker.settings.adTargeting.campaigns.filter(c => !c.segment || tracker.personalizationData.segments.includes(c.segment));
}

function bindAdSlotClick(tracker, adSlot, manager) {
   const listener = () => {
      if (!managerIsCurrent(manager)) return;
      if (document.activeElement === document.getElementById('google_ads_iframe_' + adSlot.getSlotId().getId())) {
         sendAdEvent(tracker, 'ad-click', adSlot, manager);
         window.removeEventListener('blur', listener);
         manager?.blurListeners.delete(listener);
      }
   };
   window.removeEventListener('blur', listener);
   window.addEventListener('blur', listener);
   manager?.blurListeners.add(listener);
}

function sendAdEvent(tracker, eventName, adSlot, manager) {
   if (manager && !managerIsCurrent(manager)) return;
   const data = {ad_source: 'gam'};
   const response = adSlot.getResponseInformation();

   for (const name in response) {
      if (Object.hasOwn(response, name) && response[name] && response[name]) {
         data[name] = response[name].toString();
      }
   }

   window.googletag.pubads().getTargetingKeys().forEach(key => {
      data['targeting-' + key] = window.googletag.pubads().getTargeting(key);
   });

   // prepare event data and send ad-level event
   const api = manager?.api || tracker.api;
   const eventData = api.prepareEventData(eventName, data);
   eventData.meta.page_source = eventData.source;
   eventData.meta.page_content_id = eventData.content_id;
   eventData.source = 'gam';
   eventData.content_id = response?.creativeId.toString() || '';

   api.sendRawEvent(eventData);
}

function isSlotInterstitial(slot, tracker) {
   if (slot.getOutOfPage()) {
      return true;
   }

   const adUnitPath = slot.getAdUnitPath().toLowerCase();

   // Always check for the default "interstitial" string first
   if (adUnitPath.includes('interstitial')) {
      return true;
   }

   // Then check for custom interstitial patterns from settings if configured
   if (tracker &&
      tracker.settings &&
      tracker.settings.adTargeting &&
      tracker.settings.adTargeting.interstitialAdUnitPaths &&
      Array.isArray(tracker.settings.adTargeting.interstitialAdUnitPaths) &&
      tracker.settings.adTargeting.interstitialAdUnitPaths.length > 0) {

      // Check if any of the configured patterns match the ad unit path
      for (const pattern of tracker.settings.adTargeting.interstitialAdUnitPaths) {
         if (adUnitPath.includes(pattern.toLowerCase())) {
            return true;
         }
      }
   }

   return false;
}

function bindSlotRenderEnded(tracker, manager) {
   const pubads = window.googletag.pubads();
   const listener = function (event) {
      if (!managerIsCurrent(manager)) return;
      if (!event.isEmpty && event.slot) {
         bindAdSlotClick(tracker, event.slot, manager);
      }

      // Check if the slot is an OutOfPage ad and has a response
      if (event.slot && isSlotInterstitial(event.slot, tracker) && event.slot.getResponseInformation() != null) {
         disableLeadGenPopups(tracker, manager);
      }
   };
   pubads.addEventListener('slotRenderEnded', listener);
   manager?.pubadsListeners.add({pubads, event: 'slotRenderEnded', listener});
}

function disableLeadGenPopups(tracker, manager) {
   if (!managerIsCurrent(manager)) return;
   tracker.disablePopups();
}


function isInView(elem) {
   var bounding = elem.getBoundingClientRect();
   return (
      bounding.top >= 0 &&
      bounding.left >= 0 &&
      bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      bounding.right <= (window.innerWidth || document.documentElement.clientWidth)
   );
}


export default class GoogleAdManager {
   /**
    * @param {Tracker} tracker
    */
   constructor(tracker) {
      if (!tracker) {
         return console.error('Tracker not provided.');
      }

      this.tracker = tracker;
      this.api = tracker.api;
      this.clientShortName = tracker.clientShortName;
      this.visitorId = tracker.visitorId;
      this.disposed = false;
      this.ownedTargeting = new Map();
      this.refreshTimers = new Set();
      this.blurListeners = new Set();
      this.pubadsListeners = new Set();
      this.run();
   }

   dispose() {
      if (this.disposed) return;
      this.disposed = true;
      this.refreshTimers.forEach(timer => clearTimeout(timer));
      this.refreshTimers.clear();
      this.blurListeners.forEach(listener => window.removeEventListener('blur', listener));
      this.blurListeners.clear();
      this.pubadsListeners.forEach(({pubads, event, listener}) => pubads.removeEventListener?.(event, listener));
      this.pubadsListeners.clear();

      const pubads = window.googletag?.pubads?.();
      if (pubads) {
         this.ownedTargeting.forEach(values => {
            const owners = getTargetingOwners(pubads);
            if (owners.get(values.key) !== values) return;
            const current = pubads.getTargeting(values.key) || [];
            if (sameTargeting(current, values.after)) {
               const previous = findActivePreviousOwner(values);
               if (previous) {
                  pubads.setTargeting(values.key, previous.after);
                  owners.set(values.key, previous);
               } else {
                  pubads.setTargeting(values.key, values.before);
                  owners.delete(values.key);
               }
               return;
            }
            const remaining = current.filter(value => !values.owned.includes(value));
            if (remaining.length !== current.length) {
               pubads.setTargeting(values.key, remaining);
            }
            values.previous && (values.previous.invalidated = true);
            owners.delete(values.key);
         });
      }
      this.ownedTargeting.clear();
      const personalizationKey = `hum_${this.clientShortName}_ad_personalization`;
      if (personalizationOwners.get(personalizationKey) === this) {
         personalizationOwners.delete(personalizationKey);
         localStorage.removeItem(personalizationKey);
      }
   }

   run() {
      let tracker = this.tracker;
      if (this.disposed) return;

      const adPersonalizationKey = `hum_${this.clientShortName}_ad_personalization`;

      let campaigns = getAdCampaigns(this.tracker);
      let campaignsMatched = false;
      let allTargets = {};


      campaigns.forEach(c => {
         if (matchPattern(window.location.href, c.url_patterns) && (!c.excluded_urls || !matchPattern(window.location.href, c.excluded_urls))) {
            campaignsMatched = true;

            // Configure page-level targeting.
               const targeting = c.targeting ?? {};

               Object.keys(targeting).forEach(key => {
                  if (!targeting[key].length) return;


                  if (typeof (allTargets[key]) === 'undefined' || allTargets[key] === null) {
                     allTargets[key] = [];
                  }

                  if (Array.isArray(targeting[key])) {
                     targeting[key].forEach(v => allTargets[key].push(v));
                  } else {
                     allTargets[key].push(targeting[key]);
                  }
               });

         }
      });

      const existingAdSettings = localStorage.getItem(adPersonalizationKey);
      const currentAdSettings = JSON.stringify(allTargets);

      localStorage.setItem(adPersonalizationKey, currentAdSettings);
      personalizationOwners.set(adPersonalizationKey, this);

      if ((existingAdSettings && existingAdSettings !== currentAdSettings) ||
         (!existingAdSettings && currentAdSettings !== '{}')) {
         window.dataLayer.push({
            'event': 'hum_ad_personalization_update',
            'targeting': allTargets,
         });
      }

      if (typeof window.googletag !== 'undefined' && window.googletag) {
         let refreshRequired = false;
         const manager = this;

         window.googletag.cmd.push(function () {
            if (!managerIsCurrent(manager)) return;

            bindSlotRenderEnded(tracker, manager);

            // Check existing slots for OutOfPage ads after registering the event listener
            for (const slot of window.googletag.pubads().getSlots()) {
               if (isSlotInterstitial(slot, tracker) && slot.getResponseInformation() != null) {
                  disableLeadGenPopups(tracker, manager);
                  break;
               }
            }

            Object.keys(allTargets).forEach(key => {
               let currentTargeting = window.googletag.pubads().getTargeting(key);
               currentTargeting = [...currentTargeting];
               const owners = getTargetingOwners(window.googletag.pubads());
               let previous = owners.get(key);
               if (previous && !sameTargeting(currentTargeting, previous.after)) {
                  previous.invalidated = true;
                  owners.delete(key);
                  previous = null;
               }
               const shouldSet = currentTargeting.length !== allTargets[key].length
                  || !currentTargeting.every((value, index) => value === allTargets[key][index]);

               if (shouldSet || (previous && previous.manager !== manager)) {
                  const after = [...allTargets[key]];
                  const owned = [...new Set([
                     ...(previous?.owned || []),
                     ...after.filter(value => !currentTargeting.includes(value)),
                  ])];
                  const record = {
                     key,
                     before: previous?.before || currentTargeting,
                     after,
                     owned,
                     previous,
                     manager,
                  };
                  manager.ownedTargeting.set(key, record);
                  owners.set(key, record);
                  if (shouldSet) window.googletag.pubads().setTargeting(key, after);
                  refreshRequired = true;
               }
            });

            if (campaignsMatched && window.googletag.pubads().getSlots().length > 0) {
               const refreshImmediately = [];
               const refreshOnDelay = [];
               for (const s of window.googletag.pubads().getSlots()) {
                  bindAdSlotClick(tracker, s, manager);

                  if (refreshRequired) {
                     //ad slots loaded before our tags, refresh them
                     if (!s.getResponseInformation()) {
                        //no ad rendered, see if it was attempted
                        if (document.getElementById(s.getSlotId().getDomId()).querySelector('*')) {
                           //if the adslot has a child element, google attempted to fill the ad but could not
                           refreshImmediately.push(s);
                        }
                     } else {
                        if (isInView(document.getElementById(s.getSlotId().getDomId()))) {
                           //since slots were rendered before our code fired, any visible slots with an Ad, we'll record a viewable event for
                           sendAdEvent(tracker, 'ad-impression-viewable', s, manager);
                        }
                        //after refreshExistingImpressionSecond sec if ad was rendered so the current impression has time to be viewed
                        refreshOnDelay.push(s);
                     }
                  }
               }
               if (tracker.settings.adTargeting.refreshAdSlots && refreshRequired) {
                  if (refreshImmediately.length > 0) {
                     window.googletag.pubads().refresh(refreshImmediately, {changeCorrelator: false});
                  }
                  if (refreshOnDelay.length > 0) {
                     const timer = setTimeout(() => {
                        manager.refreshTimers.delete(timer);
                        if (managerIsCurrent(manager)) window.googletag.pubads().refresh(refreshOnDelay, {changeCorrelator: false});
                     }, tracker.settings.adTargeting.refreshExistingImpressionSeconds * 1000);
                     manager.refreshTimers.add(timer);
                  }
               }

            }
         });

      }
   }
}
